import { motion, useReducedMotion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/cn";
import { fmt, pct } from "@/lib/format";
import type { BurnRow as BurnRowData } from "@/api/types";
import { Badge } from "./Badge";
import { PlanBadge } from "./PlanBadge";

export interface BurnRowProps {
  row: BurnRowData;
  rank: number;
  index?: number;
}

const BAND_FILL: Record<BurnRowData["band"], string> = {
  safe: "from-[color:var(--color-brand-1)] to-[color:var(--color-brand-2)]",
  warn: "from-amber-400 to-[color:var(--color-gold-2)]",
  over: "from-rose-500 to-[color:var(--color-brand-3)]",
};

const BAND_GLOW: Record<BurnRowData["band"], string> = {
  safe:
    "border-[color:var(--color-brand-1)]/20 shadow-[0_0_0_1px_rgba(167,139,250,0.08),0_8px_24px_-12px_rgba(167,139,250,0.3)]",
  warn:
    "border-[color:var(--color-gold-2)]/25 shadow-[0_0_0_1px_rgba(251,191,36,0.10),0_8px_24px_-12px_rgba(251,191,36,0.35)]",
  over:
    "border-rose-500/40 shadow-[0_0_0_1px_rgba(244,63,94,0.15),0_8px_28px_-10px_rgba(244,63,94,0.5)]",
};

const BAND_SPARK: Record<BurnRowData["band"], string> = {
  safe: "var(--color-brand-1)",
  warn: "var(--color-gold-2)",
  over: "var(--color-brand-3)",
};

const MEDAL_TONE: Record<1 | 2 | 3, string> = {
  1: "bg-[color:var(--color-gold-2)]/15 text-[color:var(--color-gold-2)] border-[color:var(--color-gold-2)]/40 shadow-[0_0_12px_-2px_var(--color-gold-2)]",
  2: "bg-[color:var(--color-silver-2)]/15 text-[color:var(--color-silver-2)] border-[color:var(--color-silver-2)]/40 shadow-[0_0_10px_-3px_var(--color-silver-2)]",
  3: "bg-[color:var(--color-bronze-2)]/15 text-[color:var(--color-bronze-2)] border-[color:var(--color-bronze-2)]/40 shadow-[0_0_10px_-3px_var(--color-bronze-2)]",
};

/** Deterministic 7-point stub derived from row totals. Used purely as a
 *  visual trend cue until the API returns a real daily series. */
function stubSeries(row: BurnRowData): number[] {
  const seed = (row.uid.charCodeAt(0) || 7) + row.used;
  const base = Math.max(1, row.used / 30);
  return Array.from({ length: 7 }, (_, i) => {
    const noise = ((seed * (i + 1)) % 17) / 17;
    return base * (0.7 + noise * 0.8);
  });
}

function Sparkline({ values, color }: { values: number[]; color: string }) {
  const w = 40;
  const h = 16;
  const max = Math.max(...values, 1);
  const min = Math.min(...values);
  const span = max - min || 1;
  const step = w / Math.max(values.length - 1, 1);
  const points = values
    .map((v, i) => {
      const x = i * step;
      const y = h - ((v - min) / span) * h;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      aria-hidden
      className="flex-shrink-0"
    >
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth={1.25}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

function RankPill({ rank }: { rank: number }) {
  const isMedal = rank >= 1 && rank <= 3;
  const tone = isMedal ? MEDAL_TONE[rank as 1 | 2 | 3] : "";
  return (
    <div
      aria-label={`Posición ${rank}`}
      className={cn(
        "flex h-8 w-10 flex-shrink-0 items-center justify-center rounded-full border text-xs font-bold tabular",
        isMedal
          ? tone
          : "border-white/5 bg-white/5 text-text-3",
      )}
    >
      {isMedal ? rank : `#${rank}`}
    </div>
  );
}

/** Videogame-style player card for the Burn tab. Renders rank, avatar,
 *  plan tier, XP-bar consumption, projection and 7-day sparkline. */
export function BurnRow({ row, rank, index = 0 }: BurnRowProps) {
  const reduce = useReducedMotion();
  const clamped = Math.max(0, Math.min(100, row.pct));
  const initial = (row.username ?? "?").charAt(0).toUpperCase();
  const series = stubSeries(row);
  const last = series[series.length - 1] ?? 0;
  const first = series[0] ?? 0;
  const trend = last - first;
  const sparkColor = BAND_SPARK[row.band];
  const dailyRate = row.used > 0 ? row.used / 30 : 0;

  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 6 }}
      animate={reduce ? undefined : { opacity: 1, y: 0 }}
      transition={{
        duration: 0.28,
        delay: reduce ? 0 : Math.min(index * 0.03, 0.45),
        ease: "easeOut",
      }}
      whileHover={reduce ? undefined : { y: -2 }}
      className={cn(
        "group relative grid items-center gap-4 rounded-[16px] border px-4 py-3 backdrop-blur-sm",
        "bg-white/[0.025] transition-[background-color,border-color,box-shadow] duration-200",
        "grid-cols-[auto_auto_minmax(0,1.4fr)_minmax(0,2fr)_minmax(0,1fr)_auto]",
        "hover:bg-white/[0.045]",
        BAND_GLOW[row.band],
        row.band === "over" && "burn-row-pulse",
      )}
    >
      <RankPill rank={rank} />

      <div
        aria-hidden
        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border border-white/5 bg-white/5 text-sm font-semibold text-text-2"
      >
        {initial}
      </div>

      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate font-semibold text-text-1">
            {row.username}
          </span>
          <PlanBadge plan={row.plan} />
        </div>
        <div className="mt-0.5 flex items-center gap-1.5">
          {row.team && (
            <span className="truncate text-xs text-text-3">{row.team}</span>
          )}
          {row.badges.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {row.badges.map((b) => (
                <Badge key={b} code={b} />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="min-w-0">
        <div className="mb-1 flex items-baseline justify-between gap-2 text-[10px] tabular text-text-3">
          <span className="font-semibold text-text-2">
            {fmt(row.used)}
            <span className="text-text-4"> / {fmt(row.cap)}</span>
          </span>
          <span
            className={cn(
              "font-bold",
              row.band === "over" && "text-rose-300",
              row.band === "warn" && "text-[color:var(--color-gold-2)]",
              row.band === "safe" && "text-[color:var(--color-brand-1)]",
            )}
          >
            {pct(row.pct)}
          </span>
        </div>
        <div
          role="progressbar"
          aria-valuenow={clamped}
          aria-valuemin={0}
          aria-valuemax={100}
          className="relative h-2.5 w-full overflow-hidden rounded-full bg-white/[0.06]"
        >
          <div
            className={cn(
              "relative h-full rounded-full bg-gradient-to-r",
              BAND_FILL[row.band],
            )}
            style={{ width: `${clamped}%` }}
          >
            {row.band === "over" && !reduce && (
              <span className="burn-row-shimmer absolute inset-0 rounded-full" />
            )}
          </div>
          {/* Segment ticks at 70 / 90 / 100 */}
          {[70, 90, 100].map((t) => (
            <span
              key={t}
              aria-hidden
              className="absolute top-0 h-full w-px bg-white/15"
              style={{ left: `${t}%` }}
            />
          ))}
        </div>
      </div>

      <div className="flex flex-col items-end gap-0.5 text-right">
        <div className="tabular text-sm font-bold font-display text-text-1">
          {fmt(row.projected_month_end)}
        </div>
        <div className="text-[9px] uppercase tracking-wider text-text-4">
          proy. fin mes
        </div>
        <div className="mt-0.5 flex items-center gap-1.5">
          <Sparkline values={series} color={sparkColor} />
          <span
            className={cn(
              "text-[9px] uppercase tabular tracking-wider",
              trend > 0 ? "text-rose-300" : "text-text-4",
            )}
          >
            {fmt(dailyRate)}/d
          </span>
        </div>
      </div>

      <ChevronRight
        size={16}
        aria-hidden
        className="flex-shrink-0 text-text-4 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
      />
    </motion.div>
  );
}
